import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { news } from '@/mocks/news';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';

const PAGE_SIZE = 5;

export default function News() {
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [keyword]);

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    const list = kw ? news.filter((n) => n.title.includes(kw)) : news;
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="w-full">
      <section className="bg-primary-950 text-background-50">
        <div className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-14">
          <Breadcrumb
            className="mb-5 [&_a]:text-background-200/80 [&_a:hover]:text-accent-400 [&_span]:text-background-50"
            items={[{ label: '首页', to: '/' }, { label: '影视资讯' }]}
          />
          <h1 className="text-2xl md:text-4xl font-bold">影视资讯</h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-background-200/85 leading-relaxed">
            新片定档、主创访谈、深度解读与行业观察。这里汇集了与电影《聚会的目的》及相关作品有关的最新动态，
            帮你在观影之外，看到更多幕后与思考。
          </p>

          <div className="mt-6 flex items-center gap-2 rounded-md bg-background-50 px-4 h-12 max-w-md">
            <i className="ri-search-line text-foreground-400 text-lg w-5 h-5 flex items-center justify-center"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索资讯标题"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => setKeyword('')}
                aria-label="清空搜索"
                className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-12">
        {pageItems.length > 0 ? (
          <>
            <p className="text-sm text-foreground-500 mb-5">
              {keyword ? (
                <>
                  关于「<span className="text-foreground-900 font-medium">{keyword}</span>」的资讯共{' '}
                  <span className="text-primary-600 font-semibold">{filtered.length}</span> 条
                </>
              ) : (
                <>
                  共 <span className="text-primary-600 font-semibold">{filtered.length}</span> 条资讯
                </>
              )}
            </p>

            <div className="space-y-5">
              {pageItems.map((n) => (
                <article
                  key={n.id}
                  className="group rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
                >
                  <Link to={`/news/detail/${n.id}`} className="flex flex-col sm:flex-row gap-0">
                    <span className="w-full sm:w-64 md:w-72 shrink-0 aspect-[16/9] sm:aspect-auto sm:h-auto overflow-hidden bg-foreground-100">
                      <img
                        src={n.cover}
                        alt={n.title}
                        title={`${n.title} - 影视资讯`}
                        className="w-full h-full sm:h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="flex-1 min-w-0 p-5 md:p-6">
                      <span className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
                        <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-700">
                          {n.category}
                        </span>
                        <span className="tabular-nums">{n.date}</span>
                        <span>· {n.author}</span>
                      </span>
                      <h2 className="mt-2 text-base md:text-xl font-bold text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug">
                        {n.title}
                      </h2>
                      <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {n.excerpt}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                        阅读全文
                        <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                      </span>
                    </span>
                  </Link>
                </article>
              ))}
            </div>

            <div className="mt-8 md:mt-10">
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <div className="py-16 md:py-24 text-center rounded-lg border border-dashed border-background-300 bg-background-50">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
              <i className="ri-newspaper-line text-2xl"></i>
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">暂无相关新闻</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或查看全部资讯。</p>
            <button
              type="button"
              onClick={() => setKeyword('')}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
              查看全部资讯
            </button>
          </div>
        )}
      </section>
    </div>
  );
}